import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { apiClient, type OnboardingSurvey } from "../../lib/api";
import { Btn } from "../../lib/primitives";
import { Icons } from "../../lib/icons";
import { OnboardingShell } from "./OnboardingShell";

/**
 * Step 1: three-question survey.
 *
 * Local form state seeds from the server's `survey` on mount (so a returning
 * operator sees their previous answers). On Continue, PATCH the answers +
 * `step: 'explainer'`, then navigate. The server is the source of truth for
 * `step` — the SPA's route guard reads it on subsequent loads.
 *
 * "What auth method?" is multi-select. We model it as a Set in local state
 * and serialise to an array on submit. "Both" is allowed as its own pill —
 * picking it deselects the individual options (and vice versa) to keep the
 * answer well-defined.
 */
type AppType = NonNullable<OnboardingSurvey["appType"]>;
type AuthMethod = "passkeys" | "image_points" | "both";
type Volume = NonNullable<OnboardingSurvey["monthlyVolume"]>;

const APP_OPTIONS: { value: AppType; label: string; sub: string }[] = [
  { value: "web_app", label: "Web app", sub: "Browser-first, server-rendered or SPA." },
  { value: "mobile_app", label: "Mobile app", sub: "iOS / Android client + your backend." },
  { value: "api_service", label: "API service", sub: "Headless service. Machine-to-machine." },
  { value: "ai_agent", label: "AI agent", sub: "Autonomous agent acting on a user's behalf." },
];

const VOLUME_OPTIONS: { value: Volume; label: string }[] = [
  { value: "lt_1k", label: "Less than 1k" },
  { value: "1k_10k", label: "1k – 10k" },
  { value: "gt_10k", label: "More than 10k" },
];

export function Survey() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["onboarding"],
    queryFn: apiClient.onboarding.get,
  });

  const [appType, setAppType] = useState<AppType | null>(null);
  const [authMethods, setAuthMethods] = useState<Set<AuthMethod>>(new Set());
  const [volume, setVolume] = useState<Volume | null>(null);

  useEffect(() => {
    if (!data?.survey) return;
    if (data.survey.appType) setAppType(data.survey.appType);
    if (data.survey.authMethods) setAuthMethods(new Set(data.survey.authMethods));
    if (data.survey.monthlyVolume) setVolume(data.survey.monthlyVolume);
  }, [data]);

  const submit = useMutation({
    mutationFn: () =>
      apiClient.onboarding.patch({
        step: "explainer",
        survey: {
          ...(appType ? { appType } : {}),
          authMethods: Array.from(authMethods),
          ...(volume ? { monthlyVolume: volume } : {}),
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["onboarding"] });
      navigate("/onboarding/explainer", { replace: true });
    },
  });

  function toggleAuth(method: AuthMethod) {
    setAuthMethods((prev) => {
      const next = new Set(prev);
      if (next.has(method)) {
        next.delete(method);
        return next;
      }
      // "both" is mutually exclusive with the individual options.
      if (method === "both") {
        return new Set<AuthMethod>(["both"]);
      }
      next.delete("both");
      next.add(method);
      return next;
    });
  }

  const canContinue = appType !== null && authMethods.size > 0 && volume !== null;

  return (
    <OnboardingShell current="survey">
      <div className="ad-eyebrow" style={{ marginBottom: 6 }}>
        Step 1 of 4 · About your app
      </div>
      <h1 className="ad-h1" style={{ fontSize: 28, marginBottom: 8 }}>
        Help us tailor your dashboard.
      </h1>
      <div className="ad-sub" style={{ marginBottom: 32 }}>
        Three quick questions. Nothing locked in. You can change any of this later.
      </div>

      {isLoading ? (
        <div style={{ color: "var(--ad-fg-3)", fontSize: 13 }}>Loading…</div>
      ) : (
        <div className="ad-stack-lg">
          <Section title="What are you building?">
            <div className="ad-grid-2">
              {APP_OPTIONS.map((o) => (
                <div
                  key={o.value}
                  className="ad-radio-card"
                  data-checked={appType === o.value}
                  onClick={() => setAppType(o.value)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="ad-radio-card-head">
                    <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ad-fg-1)" }}>
                      {o.label}
                    </div>
                    <div className="ad-radio-card-dot" />
                  </div>
                  <div style={{ fontSize: 12, color: "var(--ad-fg-3)" }}>{o.sub}</div>
                </div>
              ))}
            </div>
          </Section>

          <Section
            title="Which auth methods?"
            hint="Pick all that apply. Both = enroll both, require at least one."
          >
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              <AuthPill
                label="Passkeys"
                checked={authMethods.has("passkeys")}
                onClick={() => toggleAuth("passkeys")}
              />
              <AuthPill
                label="Image points"
                checked={authMethods.has("image_points")}
                onClick={() => toggleAuth("image_points")}
              />
              <AuthPill
                label="Both"
                checked={authMethods.has("both")}
                onClick={() => toggleAuth("both")}
              />
            </div>
          </Section>

          <Section title="Rough monthly auth volume?">
            <div className="ad-grid-2" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
              {VOLUME_OPTIONS.map((o) => (
                <div
                  key={o.value}
                  className="ad-radio-card"
                  data-checked={volume === o.value}
                  onClick={() => setVolume(o.value)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="ad-radio-card-head">
                    <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ad-fg-1)" }}>
                      {o.label}
                    </div>
                    <div className="ad-radio-card-dot" />
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
            <Btn
              kind="primary"
              onClick={() => submit.mutate()}
              disabled={!canContinue || submit.isPending}
              trailing={<Icons.ArrowR size={14} />}
            >
              {submit.isPending ? "Saving…" : "Continue"}
            </Btn>
          </div>
        </div>
      )}
    </OnboardingShell>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: "var(--ad-fg-1)",
          marginBottom: 4,
        }}
      >
        {title}
      </div>
      {hint && (
        <div style={{ fontSize: 12, color: "var(--ad-fg-3)", marginBottom: 10 }}>
          {hint}
        </div>
      )}
      {!hint && <div style={{ height: 10 }} />}
      {children}
    </div>
  );
}

function AuthPill({
  label,
  checked,
  onClick,
}: {
  label: string;
  checked: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "8px 14px",
        borderRadius: 999,
        border: checked
          ? "1px solid var(--ad-fg-1)"
          : "1px solid var(--ad-border-2)",
        background: checked ? "var(--ad-fg-1)" : "var(--ad-surface-1)",
        color: checked ? "var(--ad-page)" : "var(--ad-fg-2)",
        fontSize: 13,
        fontWeight: 500,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}
